import React, { useCallback, useEffect, useRef, useState } from "react";
import ClientLayoutWithOutHeader from "../../client/components/layout/ClientLayoutWithOutHeaderForTeenPatti";
import TpStudioHeader from "../../client/tp-studio/tp-studio-header";
import TpStudioLoader from "../../client/tp-studio/tp-studio-loader";
import TPStudioSidebar from "../../client/tp-studio/TPStudioSidebar";
import chip1 from "../../../assets/topstudio/images/rolet/chip1.svg";
import chip2 from "../../../assets/topstudio/images/rolet/chip2.svg";
import chip3 from "../../../assets/topstudio/images/rolet/chip3.svg";
import chip4 from "../../../assets/topstudio/images/rolet/chip4.svg";
import reconnecting from "../../../assets/topstudio/images/rolet/reconnecting.gif";
import resetIcon from "../../../assets/topstudio/images/rolet/reset-icon.svg";
import roulet from "../../../assets/topstudio/images/rolet/roulet.png";
import rouletBg from "../../../assets/topstudio/images/rolet/RouletteBg2.png";
import rouletPin from "../../../assets/topstudio/images/rolet/RoulettePin.svg";
import undoIcon from "../../../assets/topstudio/images/rolet/undo-icon.svg";
import useSocket from "../../../hooks/useSocket";
import "../../../assets/topstudio/styles/roulet.css";
import axiosInstance from "../../../utils/axiosInstance";
import GameTimer from "./components/GameTimer";
import RouletteRules from "./pop-up/roulette-rules";
import Volume from "./Volume";

import { useDispatch, useSelector } from "react-redux";
import { setUserData } from "../../../store/userSlice";
import RouletteResult from "./tp-result/RouletteResult.jsx";

const EURO_WHEEL_ORDER = [
  0, 32, 15, 19, 4, 21, 2, 25, 17, 34, 6, 27, 13, 36, 11, 30, 8, 23, 10, 5, 24,
  16, 33, 1, 20, 14, 31, 9, 22, 18, 29, 7, 28, 12, 35, 3, 26
];

const ROULETTE_SLOT_DEG = 360 / EURO_WHEEL_ORDER.length;

const ROULETTE_PIN_ANGLE_CW_FROM_TOP = 0;

const ROULETTE_WHEEL_IMAGE_OFFSET_DEG = -ROULETTE_SLOT_DEG / 2;

function isEmptyRouletteSpinSource(v) {
  if (v == null || v === "") return true;
  if (Array.isArray(v) && v.length === 0) return true;
  return false;
}

function hasConcreteRouletteSpinPayload(raw) {
  if (raw === undefined || raw === null) return false;
  if (raw === "") return false;
  if (typeof raw === "string" && raw.trim() === "") return false;
  if (typeof raw === "object" && !Array.isArray(raw)) {
    if (Object.keys(raw).length === 0) return false;
  }
  if (Array.isArray(raw) && raw.length === 0) return false;
  return true;
}

function isRowRouletteResultUnset(rowResult) {
  if (rowResult === undefined || rowResult === null) return true;
  if (rowResult === "") return true;
  if (typeof rowResult === "string" && rowResult.trim() === "") return true;
  if (Array.isArray(rowResult) && rowResult.length === 0) return true;
  if (typeof rowResult === "object" && !Array.isArray(rowResult)) {
    if (Object.keys(rowResult).length === 0) return true;
  }
  return false;
}

function extractRouletteResultNumber(payloadResult) {
  if (payloadResult == null || payloadResult === "") return null;
  if (typeof payloadResult === "number" && Number.isFinite(payloadResult)) {
    return payloadResult;
  }
  if (typeof payloadResult === "string") {
    const t = payloadResult.trim();
    if (t === "") return null;
    const n = Number(t);
    return Number.isNaN(n) ? null : n;
  }
  if (Array.isArray(payloadResult)) {
    if (payloadResult.length === 0) return null;
    const fromLast = extractRouletteResultNumber(
      payloadResult[payloadResult.length - 1]
    );
    if (fromLast !== null) return fromLast;
    return extractRouletteResultNumber(payloadResult[0]);
  }
  if (typeof payloadResult === "object") {
    const vals = Object.values(payloadResult);
    if (vals.length === 0) return null;
    const fromLast = extractRouletteResultNumber(vals[vals.length - 1]);
    if (fromLast !== null) return fromLast;
    return extractRouletteResultNumber(vals[0]);
  }
  return null;
}

const ROULETTE_RED = [
  1, 3, 5, 7, 9, 12, 14, 16, 18, 19, 21, 23, 25, 27, 30, 32, 34, 36
];
const ROULETTE_BLACK = [
  2, 4, 6, 8, 10, 11, 13, 15, 17, 20, 22, 24, 26, 28, 29, 31, 33, 35
];
const ROULETTE_COL1 = [1, 4, 7, 10, 13, 16, 19, 22, 25, 28, 31, 34];
const ROULETTE_COL2 = [2, 5, 8, 11, 14, 17, 20, 23, 26, 29, 32, 35];
const ROULETTE_COL3 = [3, 6, 9, 12, 15, 18, 21, 24, 27, 30, 33, 36];

const ROULETTE_OUTSIDE_POCKETS = {
  b37: ROULETTE_RED,
  b38: ROULETTE_BLACK,
  b39: Array.from({ length: 18 }, (_, i) => (i + 1) * 2),
  b40: Array.from({ length: 18 }, (_, i) => i * 2 + 1),
  b41: Array.from({ length: 18 }, (_, i) => i + 1),
  b42: Array.from({ length: 18 }, (_, i) => i + 19),
  b43: Array.from({ length: 12 }, (_, i) => i + 1),
  b44: Array.from({ length: 12 }, (_, i) => i + 13),
  b45: Array.from({ length: 12 }, (_, i) => i + 25),
  b46: ROULETTE_COL1,
  b47: ROULETTE_COL2,
  b48: ROULETTE_COL3
};

/** Comma-separated pocket numbers covered by this bet (for DB `board_numbers`). */
function buildRouletteBoardNumbers(bet) {
  const betType = bet.betType;
  if (!betType) return "";

  if (betType.startsWith("c_")) {
    return betType.slice(2);
  }

  if (betType.startsWith("b")) {
    const sel = parseInt(betType.slice(1), 10);
    if (!Number.isNaN(sel) && sel >= 0 && sel <= 36) {
      return String(sel);
    }
    const pockets = ROULETTE_OUTSIDE_POCKETS[betType];
    if (pockets?.length) {
      return pockets.join(",");
    }
  }

  return "";
}

function rouletteBetSelection(bet) {
  if (bet.betType.startsWith("b")) {
    return parseInt(bet.betType.slice(1), 10);
  }
  if (bet.betType.startsWith("c_")) {
    const first = parseInt(bet.betType.slice(2).split(",")[0], 10);
    return Number.isNaN(first) ? 0 : first;
  }
  return 0;
}

export default function Roulette() {
  const dispatch = useDispatch();
  const sessionUser = useSelector((s) => s.userStore.data);
  const [showRules, setShowRules] = useState(false);
  const [open, setOpen] = useState(false);
  const [betAmount, setBetAmount] = useState(100);
  const [resultList, setResultList] = useState([]);
  const [roundId, setRoundId] = useState("");
  const [resultData, setResultData] = useState([]);

  /** ---------------- SOCKET ---------------- */
  const { data } = useSocket({
    marketId: null,
    mtype: "teenpatti",
    type: "roulette"
  });
  //console.log("Socket data:", data?.data?.data);

  if (data?.data?.data?.result) {
    //console.log("result->:", data?.data?.data?.result);
  }
  const payload = data?.data?.data;

  const historyResults = data?.data?.result;

  const rowGameResult = data?.data?.data?.result ?? payload?.result;
  //console.log("rowGameResult", rowGameResult);

  const heldResultRef = useRef(0);
  const rawResult = data?.data?.data?.result;
  //console.log("[ROULETTE DEBUG] rawResult (data.data):", rawResult, "| type:", typeof rawResult);
  if (
    rawResult !== undefined &&
    rawResult !== null &&
    rawResult !== "" &&
    !(typeof rawResult === "string" && rawResult.trim() === "") &&
    !(
      typeof rawResult === "object" &&
      !Array.isArray(rawResult) &&
      Object.keys(rawResult).length === 0
    )
  ) {
    heldResultRef.current = rawResult;
  }
  const payloadResultForSpin = heldResultRef.current;
  //console.log("[ROULETTE DEBUG] payloadResultForSpin:", payloadResultForSpin);

  const payloadResult = !isEmptyRouletteSpinSource(historyResults)
    ? historyResults
    : rowGameResult;

  // --- Column definitions (needed by getWinningPositions) ---
  const col1 = ROULETTE_COL1;
  const col2 = ROULETTE_COL2;
  const col3 = ROULETTE_COL3;

  // --- Winning positions: derived from the result number itself ---
  const RED_NUMBERS = ROULETTE_RED;

  const getWinningPositionsFromNumber = (num) => {
    if (num == null || num < 0 || num > 36) return [];
    const positions = [`b${num}`];
    if (num === 0) return positions; // 0 → only the number itself wins
    // Column 2:1
    if (col1.includes(num)) positions.push("b46");
    else if (col2.includes(num)) positions.push("b47");
    else if (col3.includes(num)) positions.push("b48");
    // Red / Black
    if (RED_NUMBERS.includes(num)) positions.push("b37");
    else positions.push("b38");
    // Even / Odd
    if (num % 2 === 0) positions.push("b39");
    else positions.push("b40");
    // 1-18 / 19-36
    if (num <= 18) positions.push("b41");
    else positions.push("b42");
    // Dozens
    if (num <= 12) positions.push("b43");
    else if (num <= 24) positions.push("b44");
    else positions.push("b45");
    return positions;
  };

  const latestResult =
    Array.isArray(resultList) && resultList.length > 0
      ? resultList[resultList.length - 1]
      : null;

  const rawResultForTrophy = data?.data?.data?.result;
  const trophyNumber =
    rawResultForTrophy != null
      ? extractRouletteResultNumber(rawResultForTrophy)
      : latestResult != null
        ? Number(latestResult)
        : null;
  const winningPositions =
    trophyNumber != null && trophyNumber >= 0 && trophyNumber <= 36
      ? getWinningPositionsFromNumber(trophyNumber)
      : [];

  /*console.log(
    "[TROPHY DEBUG] trophyNumber:",
    trophyNumber,
    "| winningPositions:",
    winningPositions
  );*/

  const TrophyIcon = ({ show }) => {
    if (!show) return null;
    return (
      <span className="rou-trophy-icon">
        <i className="fa fa-trophy" />
      </span>
    );
  };

  const CHIP_COLORS = {
    100: "#EF8625",
    200: "#2F971E",
    500: "#9A2187",
    1000: "#E32443"
  };
  const [chipColorMap, setChipColorMap] = useState({});
  const [placedBets, setPlacedBets] = useState([]);
  const [displayLoader, setDisplayLoader] = useState(true);
  const displayRunnerName = (bet) => {
    const typeLabel = bet.rnr || bet.teamname || "";
    const nums = bet.board_numbers;

    const label =
      nums && typeLabel
        ? `${typeLabel} (${nums})`
        : nums || typeLabel;

    return label;
  };

  const updateUserData = async (userData) => {
    if (!userData || !sessionUser) return;
    await dispatch(
      setUserData({
        ...sessionUser,
        fname: userData.fname ?? sessionUser.fname,
        uname: userData.uname ?? sessionUser.uname,
        bz_balance: userData.bz_balance ?? sessionUser.bz_balance,
        opin_bal: userData.opin_bal ?? sessionUser.opin_bal,
        user_role: sessionUser.user_role,
        _id: sessionUser._id
      })
    );
  };

  async function getPlacedBets() {
    if (!oddsData?.evt_id) return;
    const placedBets = await axiosInstance.post("/casino/get-placed-bets", {
      cat_mid: oddsData.evt_id,
      gametype: "roulette"
    });

    setPlacedBets(placedBets.data);
  }
  const [oddsData, setOddsData] = useState(null);

  useEffect(() => {
    if (!payload) return;

    if (data?.user) {
      updateUserData(data.user);
    }

    setDisplayLoader(false);
    setOddsData(payload);
    setRoundId(payload.evt_id);
  }, [payload]);

  useEffect(() => {
    if (!payloadResult) return;

    if (payloadResult !== "") {
      //console.log("payloadResult->", payloadResult);
      setResultList([]);
      setResultList(payloadResult);
    }
  }, [payloadResult]);

  const showResultDetail = async () => {
    //console.log("INSIDE showResultDetail");
    let resultObj = {};
    resultObj.game_type = "roulette";

    //console.log("BEFORE API CALL");

    const response = await axiosInstance.post("/teenpatti-results", resultObj);
    //console.log("AFTER API CALL");

    setResultData(response.data.results_list);
    //console.log("response->", resultData);
    //console.log("RESULT API", response.data);
  };

  /** ---------------- BET STATE ---------------- */
  const [betHistory, setBetHistory] = useState([]);
  const [betAmounts, setBetAmounts] = useState({});

  const [activeTabIndex, setActiveTabIndex] = useState(1);

  const BET_META = {
    b0: { odds: 35, teamname: "Straight up" },
    b1: { odds: 35, teamname: "Straight up" },
    b2: { odds: 35, teamname: "Straight up" },
    b3: { odds: 35, teamname: "Straight up" },
    b4: { odds: 35, teamname: "Straight up" },
    b5: { odds: 35, teamname: "Straight up" },
    b6: { odds: 35, teamname: "Straight up" },
    b7: { odds: 35, teamname: "Straight up" },
    b8: { odds: 35, teamname: "Straight up" },
    b9: { odds: 35, teamname: "Straight up" },
    b10: { odds: 35, teamname: "Straight up" },
    b11: { odds: 35, teamname: "Straight up" },
    b12: { odds: 35, teamname: "Straight up" },
    b13: { odds: 35, teamname: "Straight up" },
    b14: { odds: 35, teamname: "Straight up" },
    b15: { odds: 35, teamname: "Straight up" },
    b16: { odds: 35, teamname: "Straight up" },
    b17: { odds: 35, teamname: "Straight up" },
    b18: { odds: 35, teamname: "Straight up" },
    b19: { odds: 35, teamname: "Straight up" },
    b20: { odds: 35, teamname: "Straight up" },
    b21: { odds: 35, teamname: "Straight up" },
    b22: { odds: 35, teamname: "Straight up" },
    b23: { odds: 35, teamname: "Straight up" },
    b24: { odds: 35, teamname: "Straight up" },
    b25: { odds: 35, teamname: "Straight up" },
    b26: { odds: 35, teamname: "Straight up" },
    b27: { odds: 35, teamname: "Straight up" },
    b28: { odds: 35, teamname: "Straight up" },
    b29: { odds: 35, teamname: "Straight up" },
    b30: { odds: 35, teamname: "Straight up" },
    b31: { odds: 35, teamname: "Straight up" },
    b32: { odds: 35, teamname: "Straight up" },
    b33: { odds: 35, teamname: "Straight up" },
    b34: { odds: 35, teamname: "Straight up" },
    b35: { odds: 35, teamname: "Straight up" },
    b36: { odds: 35, teamname: "Straight up" },
    b37: { odds: 1, teamname: "Red" },
    b38: { odds: 1, teamname: "Black" },
    b39: { odds: 1, teamname: "Even" },
    b40: { odds: 1, teamname: "Odd" },
    b41: { odds: 1, teamname: "1-18" },
    b42: { odds: 1, teamname: "19-36" },
    b43: { odds: 2, teamname: "Dozen Bet" },
    b44: { odds: 2, teamname: "Dozen Bet" },
    b45: { odds: 2, teamname: "Dozen Bet" },
    b46: { odds: 2, teamname: "Column Bet" },
    b47: { odds: 2, teamname: "Column Bet" },
    b48: { odds: 2, teamname: "Column Bet" }
  };

  const addToBet = (betType) => {
    if (!oddsData?.evt_id || oddsData?.evt_status !== "OPEN") return;

    const meta = BET_META[betType];
    if (!meta) return;

    setChipColorMap((prev) => ({
      ...prev,
      [betType]: CHIP_COLORS[betAmount] || "#c22220"
    }));

    setBetHistory((prev) => {
      const existingIndex = prev.findIndex(
        (b) => b.betType === betType && b.catmid === oddsData.evt_id
      );

      //  If bet already exists → ADD amount
      if (existingIndex !== -1) {
        return prev.map((b, i) =>
          i === existingIndex
            ? { ...b, amount: Number(b.amount) + Number(betAmount) }
            : b
        );
      }

      //  Otherwise create new bet

      const board_numbers = buildRouletteBoardNumbers({
        betType,
        teamname: meta.teamname
      });

      return [
        ...prev,
        {
          betType,
          amount: betAmount,
          odds: meta.odds,
          teamname: meta.teamname,
          board_numbers,
          catmid: oddsData.evt_id
        }
      ];
    });
  };

  const COMBO_ODDS = { 2: 17, 3: 11, 4: 8, 6: 5 };
  const COMBO_NAMES = {
    2: "Split Bet",
    3: "Street Bet",
    4: "Corner Bet",
    6: "Line Bet"
  };

  const addComboBet = (numbers) => {
    if (!oddsData?.evt_id || oddsData?.evt_status !== "OPEN") return;
    const sorted = [...numbers].sort((a, b) => a - b);
    const teamname = COMBO_NAMES[sorted.length] || sorted.join(",");
    const betKey = `c_${sorted.join(",")}`;
    const odds = COMBO_ODDS[sorted.length];
    if (!odds) return;

    setChipColorMap((prev) => ({
      ...prev,
      [betKey]: CHIP_COLORS[betAmount] || "#c22220"
    }));

    setBetHistory((prev) => {
      const existingIndex = prev.findIndex(
        (b) => b.betType === betKey && b.catmid === oddsData.evt_id
      );
      if (existingIndex !== -1) {
        return prev.map((b, i) =>
          i === existingIndex
            ? { ...b, amount: Number(b.amount) + Number(betAmount) }
            : b
        );
      }
      const board_numbers = buildRouletteBoardNumbers({
        betType: betKey,
        teamname
      });

      return [
        ...prev,
        {
          betType: betKey,
          amount: betAmount,
          odds,
          teamname,
          board_numbers,
          catmid: oddsData.evt_id
        }
      ];
    });
  };

  const highlightCells = (numbers) => {
    numbers.forEach((num) => {
      const el = document.getElementById(`value-${num}`);
      if (el) el.classList.add("combo-highlight");
    });
  };
  const clearHighlight = () => {
    document
      .querySelectorAll(".combo-highlight")
      .forEach((el) => el.classList.remove("combo-highlight"));
  };

  const GROUP_NUMS = {
    dozen1: ROULETTE_OUTSIDE_POCKETS.b43,
    dozen2: ROULETTE_OUTSIDE_POCKETS.b44,
    dozen3: ROULETTE_OUTSIDE_POCKETS.b45,
    low: ROULETTE_OUTSIDE_POCKETS.b41,
    high: ROULETTE_OUTSIDE_POCKETS.b42,
    even: ROULETTE_OUTSIDE_POCKETS.b39,
    odd: ROULETTE_OUTSIDE_POCKETS.b40,
    red: ROULETTE_RED,
    black: ROULETTE_BLACK
  };

  const activeTab = async (tab1) => {
    setActiveTabIndex(tab1);
  };
  /** ---------------- AUTO CALCULATE TOTALS ---------------- */
  useEffect(() => {
    const amounts = {};
    betHistory.forEach((bet) => {
      if (bet.catmid !== oddsData?.evt_id) return;
      amounts[bet.betType] = (amounts[bet.betType] || 0) + Number(bet.amount);
    });
    setBetAmounts(amounts);
  }, [betHistory, oddsData?.evt_id]);

  const undoLastBet = () => {
    if (oddsData?.evt_status !== "OPEN") return;
    setBetHistory((prev) => {
      if (prev.length === 0) return prev;
      const removed = prev[prev.length - 1];
      const rest = prev.slice(0, -1);
      const stillHasBet = rest.some((b) => b.betType === removed.betType);
      if (!stillHasBet) {
        setChipColorMap((cm) => {
          const next = { ...cm };
          delete next[removed.betType];
          return next;
        });
      }
      return rest;
    });
  };

  const resetBets = () => {
    if (oddsData?.evt_status !== "OPEN") return;
    setBetHistory([]);
    setChipColorMap({});
  };

  const betSubmittedRef = useRef(false);

  const submitBid = async () => {
    //console.log("submmit");
    if (betSubmittedRef.current) return;
    if (!oddsData?.evt_id) return;
    if (betHistory.length === 0) return;

    betSubmittedRef.current = true;

    const payload = {
      catmid: oddsData.evt_id,
      game_name: "roulette",
      market_type: "m",
      betPlaceArr: betHistory.map((bet) => ({
        amount: bet.amount,
        catmid: bet.catmid,
        market_type: "m",
        game_name: "roulette",
        odds: bet.odds ?? oddsData.b1,
        game_type: 7,
        teamname: bet.teamname,
        board_numbers: buildRouletteBoardNumbers(bet),
        bettype: "b",
        selection: rouletteBetSelection(bet)
      }))
    };

    //console.log("SUBMIT BID PAYLOAD:", payload);

    try {
      const response = await axiosInstance.post(
        "/casino/place-bet-casino-multiple",
        payload
      );
      //console.log("[roulette submitBid] response:", response.data);

      getPlacedBets();
    } catch (err) {
      console.error(err);
      betSubmittedRef.current = false; // optional retry
    }
  };
  ////////////
  const [stopBetting, setStopBetting] = useState(false);
  const prevLeftTimeRef = useRef(null);
  const lastRoundIdRef = useRef(null);
  const spinResultLockRef = useRef(null);

  useEffect(() => {
    if (!oddsData) return;

    const { evt_id, left_time, evt_status } = oddsData;

    // NEW ROUND
    if (lastRoundIdRef.current !== evt_id) {
      lastRoundIdRef.current = evt_id;
      spinResultLockRef.current = null;
      betSubmittedRef.current = false;
      prevLeftTimeRef.current = null;
      setBetHistory([]);
      setPlacedBets([]);
      setChipColorMap({});
    }

    // COUNTDOWN PHASE
    if (evt_status === "OPEN" && left_time > 0) {
      setPhase("countdown");
      setCount(left_time);
    }

    if (left_time <= 11) {
      setStopBetting(true);
    } else {
      setStopBetting(false);
    }

    //  AUTO SUBMIT WHEN TIME <= 9 SECONDS
    if (
      evt_status === "OPEN" &&
      prevLeftTimeRef.current > 11 &&
      left_time <= 11 &&
      betHistory.length > 0
    ) {
      submitBid();
    }

    prevLeftTimeRef.current = left_time;
  }, [oddsData, betHistory]);

  const remainingPathColor = "green";

  useEffect(() => {
    if (activeTabIndex === 2) {
      showResultDetail();
    }
  }, [activeTabIndex]);

  const color = (n) =>
    [
      1, 3, 5, 7, 9, 12, 14, 16, 18, 19, 21, 23, 25, 27, 30, 32, 34, 36
    ].includes(n)
      ? "col-Red"
      : "col-Black";

  function Cell({ n }) {
    return (
      <div id={`value-${n}`} className={`${color(n)} rou-cell`}>
        <span className="zero-value">{n}</span>
        {/* overlays (hidden by default) */}
        <div className="verticalOverlay RouDesleft">
          <div style={{ display: "none" }}>
            <img alt="" className="rol-winner-icon" />
            <span className="Rou-image-text">0</span>
          </div>
        </div>
        <div className="verticalOverlay RouDesRight">
          <div style={{ display: "none" }}>
            <img alt="" className="rol-winner-icon" />
            <span className="Rou-image-text">0</span>
          </div>
        </div>
        <div className="horizontaloverlay RouDesCB">
          <div style={{ display: "none" }}>
            <img alt="" className="rol-winner-icon" />
            <span className="Rou-image-text">0</span>
          </div>
        </div>
        <div className="centerboxoverlay RouDesRB">
          <div style={{ display: "none" }}>
            <img alt="" className="rol-winner-icon" />
            <span className="Rou-image-text">0</span>
          </div>
        </div>
        <div className="centerboxoverlay RouDesLB">
          <div style={{ display: "none" }}>
            <img alt="" className="rol-winner-icon" />
            <span className="Rou-image-text">0</span>
          </div>
        </div>
      </div>
    );
  }

  const [phase, setPhase] = useState("idle"); // 'idle' | 'countdown' | 'spinning'
  const [count, setCount] = useState(0);
  const [rotation, setRotation] = useState(0);
  const prevRotationRef = useRef(0);
  const spinDurationMs = 50000;

  const startRound = (seconds = 10) => {
    if (phase !== "idle") return;
    setCount(seconds);
    setPhase("countdown");
  };

  useEffect(() => {
    if (phase !== "countdown") return;

    if (count <= 0) {
      setPhase("idle");
      return;
    }

    const t = setTimeout(() => setCount((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [phase, count]);

  const spinWheel = useCallback((winningNumber) => {
    const extraTurns = 15 + Math.floor(Math.random() * 6);
    let deltaDeg;
    let stopPocket;
    let pocketCenterDeg;

    if (winningNumber !== undefined && winningNumber !== null) {
      const n = Number(winningNumber);
      if (!Number.isNaN(n) && n >= 0 && n <= 36) {
        const idx = EURO_WHEEL_ORDER.indexOf(n);
        if (idx >= 0) {
          stopPocket = n;

          pocketCenterDeg =
            idx * ROULETTE_SLOT_DEG +
            ROULETTE_SLOT_DEG / 2 +
            ROULETTE_WHEEL_IMAGE_OFFSET_DEG;

          const desiredWheelMod =
            (((ROULETTE_PIN_ANGLE_CW_FROM_TOP - pocketCenterDeg) % 360) + 360) %
            360;
          const prevMod = ((prevRotationRef.current % 360) + 360) % 360;
          deltaDeg = (desiredWheelMod - prevMod + 360) % 360;
        }
      }
    }

    if (deltaDeg === undefined) {
      return;
    }

    const target = prevRotationRef.current + extraTurns * 360 + deltaDeg;
    //console.log("Wheel will stop at:", stopPocket);

    setPhase("spinning");
    setRotation(target);
    prevRotationRef.current = target;
  }, []);

  useEffect(() => {
    if (!hasConcreteRouletteSpinPayload(payloadResultForSpin)) return;

    const n = extractRouletteResultNumber(payloadResultForSpin);
    //console.log("[ROULETTE DEBUG] extractRouletteResultNumber returned:", n, "from payloadResultForSpin:", payloadResultForSpin);
    if (n === null || n < 0 || n > 36) return;

    const evtId = oddsData?.evt_id;
    const lockKey =
      evtId != null && evtId !== ""
        ? `${evtId}|${n}`
        : `${typeof payloadResultForSpin === "object" &&
          payloadResultForSpin !== null
          ? JSON.stringify(payloadResultForSpin)
          : String(payloadResultForSpin ?? "")
        }|${n}`;

    if (spinResultLockRef.current === lockKey) return;
    spinResultLockRef.current = lockKey;

    //console.log("[ROULETTE DEBUG] >>> SPINNING WHEEL TO:", n, "| lockKey:", lockKey);
    spinWheel(n);
  }, [payloadResultForSpin, oddsData?.evt_id, spinWheel]);

  const onSpinEnd = () => {
    if (phase === "spinning") {
      setPhase("idle");
      setCount(0);
    }
  };

  startRound(3);

  useEffect(() => {
    document.documentElement.classList.add("roulette-bg-black");
    document.documentElement.classList.add("h-100");

    return () => {
      document.documentElement.classList.remove("roulette-bg-black");
      document.documentElement.classList.remove("h-100");
    };
  }, []);

  function useIsMobile(breakpoint = 768) {
    const [isMobile, setIsMobile] = useState(window.innerWidth <= breakpoint);

    useEffect(() => {
      const onResize = () => setIsMobile(window.innerWidth <= breakpoint);
      window.addEventListener("resize", onResize);
      return () => window.removeEventListener("resize", onResize);
    }, [breakpoint]);

    return isMobile;
  }
  const isMobile = useIsMobile();

  // Derive per-bet display amounts from betAmounts map
  const b0Amt = betAmounts["b0"] || 0;
  const b1Amt = betAmounts["b1"] || 0;
  const b2Amt = betAmounts["b2"] || 0;
  const b3Amt = betAmounts["b3"] || 0;
  const b4Amt = betAmounts["b4"] || 0;
  const b5Amt = betAmounts["b5"] || 0;
  const b6Amt = betAmounts["b6"] || 0;
  const b7Amt = betAmounts["b7"] || 0;
  const b8Amt = betAmounts["b8"] || 0;
  const b9Amt = betAmounts["b9"] || 0;
  const b10Amt = betAmounts["b10"] || 0;
  const b11Amt = betAmounts["b11"] || 0;
  const b12Amt = betAmounts["b12"] || 0;
  const b13Amt = betAmounts["b13"] || 0;
  const b14Amt = betAmounts["b14"] || 0;
  const b15Amt = betAmounts["b15"] || 0;
  const b16Amt = betAmounts["b16"] || 0;
  const b17Amt = betAmounts["b17"] || 0;
  const b18Amt = betAmounts["b18"] || 0;
  const b19Amt = betAmounts["b19"] || 0;
  const b20Amt = betAmounts["b20"] || 0;
  const b21Amt = betAmounts["b21"] || 0;
  const b22Amt = betAmounts["b22"] || 0;
  const b23Amt = betAmounts["b23"] || 0;
  const b24Amt = betAmounts["b24"] || 0;
  const b25Amt = betAmounts["b25"] || 0;
  const b26Amt = betAmounts["b26"] || 0;
  const b27Amt = betAmounts["b27"] || 0;
  const b28Amt = betAmounts["b28"] || 0;
  const b29Amt = betAmounts["b29"] || 0;
  const b30Amt = betAmounts["b30"] || 0;
  const b31Amt = betAmounts["b31"] || 0;
  const b32Amt = betAmounts["b32"] || 0;
  const b33Amt = betAmounts["b33"] || 0;
  const b34Amt = betAmounts["b34"] || 0;
  const b35Amt = betAmounts["b35"] || 0;
  const b36Amt = betAmounts["b36"] || 0;
  const b37Amt = betAmounts["b37"] || 0;
  const b38Amt = betAmounts["b38"] || 0;
  const b39Amt = betAmounts["b39"] || 0;
  const b40Amt = betAmounts["b40"] || 0;
  const b41Amt = betAmounts["b41"] || 0;
  const b42Amt = betAmounts["b42"] || 0;
  const b43Amt = betAmounts["b43"] || 0;
  const b44Amt = betAmounts["b44"] || 0;
  const b45Amt = betAmounts["b45"] || 0;
  const b46Amt = betAmounts["b46"] || 0;
  const b47Amt = betAmounts["b47"] || 0;
  const b48Amt = betAmounts["b48"] || 0;


  const getResultColorCss = (resultNumber) => {
    if (ROULETTE_RED.includes(resultNumber)) {
      return "col-Red-button";
    } else {
      return "col-Black-button";
    }
  };

  return (
    <div className="roulette-scope roulette-bg-black">
      <ClientLayoutWithOutHeader showLoader={false}>
        <div class="details-tabs-wrapper1">
          <div className="main-page">
            <div className="row no-gutters">
              <div className="col-md-12">
                <div className="bodyheight">
                  <div className="wrapper">
                    <TPStudioSidebar open={open} />
                  </div>

                  <div className="lobby-ctn BPGmaindiv">
                    <TpStudioHeader setOpen={setOpen} open={open} />
                    <TpStudioLoader displayLoader={displayLoader} />
                  </div>

                  <div className="card-body">
                    <div
                      className="row"
                      style={{ display: displayLoader ? "none" : "" }}
                    >
                      <div className="col-md-8">
                        <div className="footer-divbpg border-gradient-footer border-gradient-purple-footer h-45">
                          <div className="col-6 col-md-6 titlebarname">
                            <p
                              style={{ marginBottom: "-6px", marginTop: "6px" }}
                            >
                              Auto Roulette
                            </p>
                          </div>
                          <div
                            className="col-6 col-md-6 inline-block Gameidcol text-right"
                            style={{ marginTop: "4px" }}
                          >
                            <div
                              role="group"
                              aria-label="Second group"
                              className="btn-group btn-group-sm mr-2"
                            >
                              <button
                                style={{ fontSize: "14px" }}
                                type="button"
                                id="Alltab"
                                className={
                                  activeTabIndex == 1
                                    ? "tablink btn btn-primary bbactive"
                                    : "tablink btn btn-primary"
                                }
                                onClick={() => activeTab(1)}
                              >
                                Game
                              </button>
                              <button
                                style={{ fontSize: "14px" }}
                                type="button"
                                id="RESULTStab"
                                className={
                                  activeTabIndex == 2
                                    ? "tablink btn btn-primary bbactive"
                                    : "tablink btn btn-primary"
                                }
                                onClick={() => activeTab(2)}
                              >
                                Results
                              </button>
                              <button
                                style={{ fontSize: "14px" }}
                                type="button"
                                className="tablink btn btn-primary"
                                onClick={() => setShowRules(true)}
                              >
                                Rules
                              </button>
                              <RouletteRules
                                show={showRules}
                                onClose={() => setShowRules(false)}
                              />
                            </div>
                          </div>
                        </div>

                        {activeTabIndex == 1 && (
                          <>
                            <div
                              className="row"
                              style={{
                                display: activeTabIndex == 1 ? "" : "none"
                              }}
                            >
                              <div className="col-2 col-md-1">
                                <div className="Connectedclients mt-1">
                                  <i
                                    aria-hidden="true"
                                    className="fa fa-user mr-1"
                                    style={{
                                      fontSize: "14px",
                                      color: "white"
                                    }}
                                  />
                                  <b>
                                    <span id="UsersCount" className="number">
                                      57
                                    </span>
                                  </b>
                                </div>
                              </div>
                            </div>
                            <div className="cards-wrapper">
                              <div className="font-familyAVE roulette-bgNew">
                                <div className="col-12 timerDiv2">
                                  <GameTimer leftTime={oddsData?.left_time} />
                                </div>

                                {/* Roulette Main Section */}
                                <div className="cards-layout">
                                  <div className="roulli-wheel">
                                    <img
                                      src={rouletPin}
                                      alt="PIN"
                                      className="roulette-pinR"
                                    />
                                    <img
                                      id="rouletteWheel"
                                      src={roulet}
                                      alt="roulette"
                                      className="roulette-wheelR"
                                      style={{
                                        transform: `rotate(${rotation}deg)`,
                                        transition:
                                          phase === "spinning"
                                            ? `transform ${spinDurationMs}ms cubic-bezier(0.1, 0.7, 0.2, 1)`
                                            : "none"
                                      }}
                                      onTransitionEnd={onSpinEnd}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div
                              style={{
                                textAlign: "right",
                                marginRight: "20px",
                                display: activeTabIndex == 1 ? "" : "none"
                              }}
                              className="volume-wrapper"
                            >
                              <Volume />
                            </div>

                            <center>
                              <div
                                className="footer-divbpg border-gradient-footer border-gradient-purple-footer"
                                style={{ marginTop: "10px" }}
                              >
                                <div className="buttons-div">
                                  {Object.keys(resultList).map((idx, index) =>
                                    index < 10 ? (
                                      <button
                                        className={getResultColorCss(
                                          resultList[idx]
                                        )}
                                      >
                                        {resultList[idx]}
                                      </button>
                                    ) : (
                                      ""
                                    )
                                  )}
                                </div>
                                <div className="round-div">
                                  <div className="label">Round ID:</div>
                                  <div className="id">{roundId}</div>
                                </div>
                              </div>
                            </center>

                            <div
                              className="roul-box-container position-relative"
                              style={{
                                display: activeTabIndex == 1 ? "" : "none"
                              }}
                            >
                              <div
                                className={`flex-fill h-100 w-100 absolute overlayRoulettelock ${
                                  stopBetting ||
                                  oddsData?.evt_status == "CLOSED"
                                    ? ""
                                    : "hides"
                                }`}
                              >
                                <div style={{ textAlign: "center" }}>
                                  <svg
                                    focusable="false"
                                    aria-hidden="true"
                                    viewBox="0 0 24 24"
                                    data-testid="LockIcon"
                                    className="MuiSvgIcon-root MuiSvgIcon-fontSizeMedium lock-icon css-vubbuv"
                                    style={{
                                      width: "70px",
                                      filter: "invert(0)"
                                    }}
                                  >
                                    <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z" />
                                  </svg>
                                </div>
                              </div>
                              {!isMobile ? (
                                <div className="roul-box-desktop">
                                  <div className="chip-odds-view-ctn">
                                    <div className="chip-odds-view-layer">
                                      <div className="t-l-ctn col-3">
                                        <div className="total-bets">
                                          <span className="text">
                                            Total Bet
                                          </span>
                                          <span className="t-count">0</span>
                                        </div>
                                      </div>

                                      <div className="chipDeck col-6">
                                        <div className="chip-ctn">
                                          <div
                                            id="fadeIn"
                                            className="show-stakes"
                                          >
                                            <div className="chips-layer">
                                              <div
                                                className={`cdChip ${
                                                  betAmount === 100
                                                    ? "dream_zoomed_chip Active"
                                                    : "noActive"
                                                }`}
                                                onClick={() =>
                                                  setBetAmount(100)
                                                }
                                              >
                                                <img
                                                  src={chip1}
                                                  alt=""
                                                  className="chipimg"
                                                />
                                                <span className="cdChipSpan">
                                                  100
                                                </span>
                                              </div>
                                              <div
                                                className={`cdChip ${
                                                  betAmount === 200
                                                    ? "dream_zoomed_chip Active"
                                                    : "noActive"
                                                }`}
                                                onClick={() =>
                                                  setBetAmount(200)
                                                }
                                              >
                                                <img
                                                  src={chip2}
                                                  alt=""
                                                  className="chipimg"
                                                />
                                                <span className="cdChipSpan">
                                                  200
                                                </span>
                                              </div>
                                              <div
                                                className={`cdChip ${
                                                  betAmount === 500
                                                    ? "dream_zoomed_chip Active"
                                                    : "noActive"
                                                }`}
                                                onClick={() =>
                                                  setBetAmount(500)
                                                }
                                              >
                                                <img
                                                  src={chip3}
                                                  alt=""
                                                  className="chipimg"
                                                />
                                                <span className="cdChipSpan">
                                                  500
                                                </span>
                                              </div>
                                              <div
                                                className={`cdChip ${
                                                  betAmount === 1000
                                                    ? "dream_zoomed_chip Active"
                                                    : "noActive"
                                                }`}
                                                onClick={() =>
                                                  setBetAmount(1000)
                                                }
                                              >
                                                <img
                                                  src={chip4}
                                                  alt=""
                                                  className="chipimg"
                                                />
                                                <span className="cdChipSpan">
                                                  1k
                                                </span>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      </div>

                                      <div className="ure-btn col-3">
                                        <div
                                          className="undo-ctn"
                                          onClick={undoLastBet}
                                        >
                                          <img
                                            src={undoIcon}
                                            alt=""
                                            className="undo-icon"
                                          />
                                          <span>Undo</span>
                                        </div>
                                        <div
                                          className="reset-ctn"
                                          onClick={resetBets}
                                        >
                                          <img
                                            src={resetIcon}
                                            alt=""
                                            className="reset-icon"
                                          />
                                          <span>Reset</span>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <div id="mobileChipOdds">
                                  <div className="roul-box-desktop">
                                    <div className="chip-odds-view-ctn">
                                      <div className="chip-odds-view-layer">
                                        <div className="t-l-ctn col-6">
                                          <div className="total-bets">
                                            <span className="text">
                                              Total Bet
                                            </span>
                                            <span className="t-count">0</span>
                                          </div>
                                        </div>
                                        <div className="ure-btn col-6">
                                          <div
                                            className="undo-ctn"
                                            onClick={undoLastBet}
                                          >
                                            <img
                                              src={undoIcon}
                                              alt=""
                                              className="undo-icon"
                                            />
                                            <span>Undo</span>
                                          </div>
                                          <div
                                            className="reset-ctn"
                                            onClick={resetBets}
                                          >
                                            <img
                                              src={resetIcon}
                                              alt=""
                                              className="reset-icon"
                                            />
                                            <span>Reset</span>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="roul-box-desktop">
                                    <div className="chip-odds-view-ctn">
                                      <div className="chip-odds-view-layer">
                                        <div className="chipDeck col-12">
                                          <div className="chip-ctn">
                                            <div
                                              id="fadeIn"
                                              className="show-stakes"
                                            >
                                              <div className="chips-layer">
                                                <div
                                                  className={`cdChip ${
                                                    betAmount === 100
                                                      ? "dream_zoomed_chip Active"
                                                      : "noActive"
                                                  }`}
                                                  onClick={() =>
                                                    setBetAmount(100)
                                                  }
                                                >
                                                  <img
                                                    src={chip1}
                                                    alt=""
                                                    className="chipimg"
                                                  />
                                                  <span className="cdChipSpan">
                                                    100
                                                  </span>
                                                </div>
                                                <div
                                                  className={`cdChip ${
                                                    betAmount === 200
                                                      ? "dream_zoomed_chip Active"
                                                      : "noActive"
                                                  }`}
                                                  onClick={() =>
                                                    setBetAmount(200)
                                                  }
                                                >
                                                  <img
                                                    src={chip2}
                                                    alt=""
                                                    className="chipimg"
                                                  />
                                                  <span className="cdChipSpan">
                                                    200
                                                  </span>
                                                </div>
                                                <div
                                                  className={`cdChip ${
                                                    betAmount === 500
                                                      ? "dream_zoomed_chip Active"
                                                      : "noActive"
                                                  }`}
                                                  onClick={() =>
                                                    setBetAmount(500)
                                                  }
                                                >
                                                  <img
                                                    src={chip3}
                                                    alt=""
                                                    className="chipimg"
                                                  />
                                                  <span className="cdChipSpan">
                                                    500
                                                  </span>
                                                </div>
                                                <div
                                                  className={`cdChip ${
                                                    betAmount === 1000
                                                      ? "dream_zoomed_chip Active"
                                                      : "noActive"
                                                  }`}
                                                  onClick={() =>
                                                    setBetAmount(1000)
                                                  }
                                                >
                                                  <img
                                                    src={chip4}
                                                    alt=""
                                                    className="chipimg"
                                                  />
                                                  <span className="cdChipSpan">
                                                    1k
                                                  </span>
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}
                              <div className="roul-box-desktop">
                                <div className="roulette-ctn">
                                  <div
                                    id="value-0"
                                    className={`zero-ctn ${
                                      winningPositions.includes("b0")
                                        ? "rou-winner-cell"
                                        : ""
                                    }`}
                                    onClick={() => addToBet("b0")}
                                  >
                                    <TrophyIcon
                                      show={winningPositions.includes("b0")}
                                    />
                                    <span
                                      className={`dream_chips_cls ${
                                        betAmounts["b0"] ? "" : "hides"
                                      }`}
                                    >
                                      {betAmounts["b0"] || 0}
                                    </span>
                                    <span className="zero-value"> 0</span>
                                  </div>
                                  <div className="match-odds-ctn">
                                    {[col1, col2, col3].map(
                                      (colNums, rowIdx) => (
                                        <div
                                          className="first-column"
                                          key={rowIdx}
                                        >
                                          {colNums.map((n, colIdx) => {
                                            const R = rowIdx;
                                            const C = colIdx;
                                            const betKey = `b${n}`;
                                            const amt = betAmounts[betKey] || 0;
                                            const splitLeft =
                                              C > 0 ? [n - 3, n] : [0, n];
                                            const splitRight =
                                              C < 11 ? [n, n + 3] : null;
                                            let bottomBet;
                                            if (R === 2) {
                                              bottomBet = [n - 2, n - 1, n];
                                            } else if (R === 1) {
                                              bottomBet = [n, n + 1];
                                            } else {
                                              bottomBet = [n, n + 1, n + 2];
                                            }
                                            let rbBet;
                                            if (R === 2 && C < 11) {
                                              rbBet = [
                                                n - 2,
                                                n - 1,
                                                n,
                                                n + 1,
                                                n + 2,
                                                n + 3
                                              ];
                                            } else if (R === 1 && C < 11) {
                                              rbBet = [n, n + 1, n + 3, n + 4];
                                            } else if (R === 0 && C < 11) {
                                              rbBet = [n, n + 1, n + 3, n + 4];
                                            }
                                            let lbBet;
                                            if (R === 2 && C > 0) {
                                              lbBet = [
                                                n - 5,
                                                n - 4,
                                                n - 3,
                                                n - 2,
                                                n - 1,
                                                n
                                              ];
                                            } else if (R === 2 && C === 0) {
                                              lbBet = [0, 1, 2, 3];
                                            } else if (R === 1 && C > 0) {
                                              lbBet = [n - 3, n - 2, n, n + 1];
                                            } else if (R === 1 && C === 0) {
                                              lbBet = [0, n, n + 1];
                                            } else if (R === 0 && C > 0) {
                                              lbBet = [n - 3, n - 2, n, n + 1];
                                            } else if (R === 0 && C === 0) {
                                              lbBet = [0, 1, 2, 3];
                                            }
                                            const comboKey = (nums) =>
                                              `c_${[...nums]
                                                .sort((a, b) => a - b)
                                                .join(",")}`;
                                            const comboAmt = (nums) =>
                                              betAmounts[comboKey(nums)] || 0;
                                            const comboClr = (nums) =>
                                              chipColorMap[comboKey(nums)] ||
                                              "#c22220";
                                            return (
                                              <div
                                                key={n}
                                                id={`value-${n}`}
                                                className={`${color(n)} col ${
                                                  winningPositions.includes(
                                                    betKey
                                                  )
                                                    ? "rou-winner-cell"
                                                    : ""
                                                }`}
                                                onClick={() => addToBet(betKey)}
                                              >
                                                <TrophyIcon
                                                  show={winningPositions.includes(
                                                    betKey
                                                  )}
                                                />
                                                <span
                                                  className={`dream_chips_cls ${
                                                    amt ? "" : "hides"
                                                  }`}
                                                >
                                                  {amt}
                                                </span>
                                                <span className="zero-value">
                                                  {n}
                                                </span>
                                                <div
                                                  className="verticalOverlay RouDesleft"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    addComboBet(splitLeft);
                                                  }}
                                                  onMouseEnter={() =>
                                                    highlightCells(splitLeft)
                                                  }
                                                  onMouseLeave={clearHighlight}
                                                >
                                                  {comboAmt(splitLeft) > 0 && (
                                                    <span
                                                      className="dream_chips_cls overlay-chip"
                                                      style={{
                                                        background:
                                                          comboClr(splitLeft)
                                                      }}
                                                    >
                                                      {comboAmt(splitLeft)}
                                                    </span>
                                                  )}
                                                </div>
                                                {splitRight && (
                                                  <div
                                                    className="verticalOverlay RouDesRight"
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      addComboBet(splitRight);
                                                    }}
                                                    onMouseEnter={() =>
                                                      highlightCells(splitRight)
                                                    }
                                                    onMouseLeave={
                                                      clearHighlight
                                                    }
                                                  >
                                                    {comboAmt(splitRight) >
                                                      0 && (
                                                      <span
                                                        className="dream_chips_cls overlay-chip"
                                                        style={{
                                                          background:
                                                            comboClr(splitRight)
                                                        }}
                                                      >
                                                        {comboAmt(splitRight)}
                                                      </span>
                                                    )}
                                                  </div>
                                                )}
                                                <div
                                                  className="horizontaloverlay RouDesCB"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    addComboBet(bottomBet);
                                                  }}
                                                  onMouseEnter={() =>
                                                    highlightCells(bottomBet)
                                                  }
                                                  onMouseLeave={clearHighlight}
                                                >
                                                  {comboAmt(bottomBet) > 0 && (
                                                    <span
                                                      className="dream_chips_cls overlay-chip"
                                                      style={{
                                                        background:
                                                          comboClr(bottomBet)
                                                      }}
                                                    >
                                                      {comboAmt(bottomBet)}
                                                    </span>
                                                  )}
                                                </div>
                                                {rbBet && (
                                                  <div
                                                    className="centerboxoverlay RouDesRB"
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      addComboBet(rbBet);
                                                    }}
                                                    onMouseEnter={() =>
                                                      highlightCells(rbBet)
                                                    }
                                                    onMouseLeave={
                                                      clearHighlight
                                                    }
                                                  >
                                                    {comboAmt(rbBet) > 0 && (
                                                      <span
                                                        className="dream_chips_cls overlay-chip"
                                                        style={{
                                                          background:
                                                            comboClr(rbBet)
                                                        }}
                                                      >
                                                        {comboAmt(rbBet)}
                                                      </span>
                                                    )}
                                                  </div>
                                                )}
                                                {lbBet && (
                                                  <div
                                                    className="centerboxoverlay RouDesLB"
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      addComboBet(lbBet);
                                                    }}
                                                    onMouseEnter={() =>
                                                      highlightCells(lbBet)
                                                    }
                                                    onMouseLeave={
                                                      clearHighlight
                                                    }
                                                  >
                                                    {comboAmt(lbBet) > 0 && (
                                                      <span
                                                        className="dream_chips_cls overlay-chip"
                                                        style={{
                                                          background:
                                                            comboClr(lbBet)
                                                        }}
                                                      >
                                                        {comboAmt(lbBet)}
                                                      </span>
                                                    )}
                                                  </div>
                                                )}
                                              </div>
                                            );
                                          })}
                                        </div>
                                      )
                                    )}
                                  </div>
                                  <div className="columns-ctn">
                                    {[
                                      [46, col1],
                                      [47, col2],
                                      [48, col3]
                                    ].map(([bn, rowNums]) => (
                                      <div
                                        key={bn}
                                        className={`columns-layer ${
                                          winningPositions.includes(`b${bn}`)
                                            ? "rou-winner-cell"
                                            : ""
                                        }`}
                                        onClick={() => addToBet(`b${bn}`)}
                                        onMouseEnter={() =>
                                          highlightCells(rowNums)
                                        }
                                        onMouseLeave={clearHighlight}
                                        style={{ position: "relative" }}
                                      >
                                        <TrophyIcon
                                          show={winningPositions.includes(
                                            `b${bn}`
                                          )}
                                        />
                                        <span
                                          className={`dream_chips_cls ${
                                            betAmounts[`b${bn}`] ? "" : "hides"
                                          }`}
                                        >
                                          {betAmounts[`b${bn}`] || 0}
                                        </span>
                                        <span className="zero-value">2:1</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>{" "}
                                <div className="dozens-layer">
                                  <div className="dozens-ctn">
                                    <div
                                      className={`dozens ${
                                        winningPositions.includes("b43")
                                          ? "rou-winner-cell"
                                          : ""
                                      }`}
                                      onClick={() => addToBet("b43")}
                                      onMouseEnter={() =>
                                        highlightCells(GROUP_NUMS.dozen1)
                                      }
                                      onMouseLeave={clearHighlight}
                                      style={{ position: "relative" }}
                                    >
                                      <TrophyIcon
                                        show={winningPositions.includes("b43")}
                                      />
                                      {/**/}{" "}
                                      <span
                                        className={`dream_chips_cls ${
                                          b43Amt ? "" : "hides"
                                        }`}
                                      >
                                        {b43Amt}
                                      </span>
                                      <span className="zero-value">
                                        1 - 12
                                        {/**/}
                                      </span>
                                    </div>{" "}
                                    <div
                                      className={`dozens ${
                                        winningPositions.includes("b44")
                                          ? "rou-winner-cell"
                                          : ""
                                      }`}
                                      onClick={() => addToBet("b44")}
                                      onMouseEnter={() =>
                                        highlightCells(GROUP_NUMS.dozen2)
                                      }
                                      onMouseLeave={clearHighlight}
                                      style={{ position: "relative" }}
                                    >
                                      <TrophyIcon
                                        show={winningPositions.includes("b44")}
                                      />
                                      {/**/}{" "}
                                      <span
                                        className={`dream_chips_cls ${
                                          b44Amt ? "" : "hides"
                                        }`}
                                      >
                                        {b44Amt}
                                      </span>
                                      <span className="zero-value">
                                        13 - 24
                                        {/**/}
                                      </span>
                                    </div>{" "}
                                    <div
                                      className={`dozens ${
                                        winningPositions.includes("b45")
                                          ? "rou-winner-cell"
                                          : ""
                                      }`}
                                      onClick={() => addToBet("b45")}
                                      onMouseEnter={() =>
                                        highlightCells(GROUP_NUMS.dozen3)
                                      }
                                      onMouseLeave={clearHighlight}
                                      style={{ position: "relative" }}
                                    >
                                      <TrophyIcon
                                        show={winningPositions.includes("b45")}
                                      />
                                      {/**/}{" "}
                                      <span
                                        className={`dream_chips_cls ${
                                          b45Amt ? "" : "hides"
                                        }`}
                                      >
                                        {b45Amt}
                                      </span>
                                      <span className="zero-value">
                                        25 - 36
                                        {/**/}
                                      </span>
                                    </div>
                                  </div>
                                </div>{" "}
                                <div className="last-row-ctn">
                                  <div className="last-row">
                                    <div
                                      className={`low ${
                                        winningPositions.includes("b41")
                                          ? "rou-winner-cell"
                                          : ""
                                      }`}
                                      onClick={() => addToBet("b41")}
                                      onMouseEnter={() =>
                                        highlightCells(GROUP_NUMS.low)
                                      }
                                      onMouseLeave={clearHighlight}
                                      style={{ position: "relative" }}
                                    >
                                      <TrophyIcon
                                        show={winningPositions.includes("b41")}
                                      />
                                      {/**/}{" "}
                                      <span
                                        className={`dream_chips_cls ${
                                          b41Amt ? "" : "hides"
                                        }`}
                                      >
                                        {b41Amt}
                                      </span>
                                      <span className="zero-value">
                                        1 - 18
                                        {/**/}
                                      </span>
                                    </div>{" "}
                                    <div
                                      className={`even ${
                                        winningPositions.includes("b39")
                                          ? "rou-winner-cell"
                                          : ""
                                      }`}
                                      onClick={() => addToBet("b39")}
                                      onMouseEnter={() =>
                                        highlightCells(GROUP_NUMS.even)
                                      }
                                      onMouseLeave={clearHighlight}
                                      style={{ position: "relative" }}
                                    >
                                      <TrophyIcon
                                        show={winningPositions.includes("b39")}
                                      />
                                      {/**/}{" "}
                                      <span
                                        className={`dream_chips_cls ${
                                          b39Amt ? "" : "hides"
                                        }`}
                                      >
                                        {b39Amt}
                                      </span>
                                      <span className="zero-value">
                                        Even
                                        {/**/}
                                      </span>
                                    </div>{" "}
                                    <div className="red-black">
                                      <div
                                        className={`col-Red color-ctn ${
                                          winningPositions.includes("b37")
                                            ? "rou-winner-cell"
                                            : ""
                                        }`}
                                        onClick={() => addToBet("b37")}
                                        onMouseEnter={() =>
                                          highlightCells(GROUP_NUMS.red)
                                        }
                                        onMouseLeave={clearHighlight}
                                        style={{ position: "relative" }}
                                      >
                                        <TrophyIcon
                                          show={winningPositions.includes(
                                            "b37"
                                          )}
                                        />
                                        {/**/}{" "}
                                        <span
                                          className={`dream_chips_cls ${
                                            b37Amt ? "" : "hides"
                                          }`}
                                        >
                                          {b37Amt}
                                        </span>
                                        <span className="zero-value">
                                          Red
                                          {/**/}
                                        </span>
                                      </div>{" "}
                                      <div
                                        className={`col-Black color-ctn ${
                                          winningPositions.includes("b38")
                                            ? "rou-winner-cell"
                                            : ""
                                        }`}
                                        onClick={() => addToBet("b38")}
                                        onMouseEnter={() =>
                                          highlightCells(GROUP_NUMS.black)
                                        }
                                        onMouseLeave={clearHighlight}
                                        style={{ position: "relative" }}
                                      >
                                        <TrophyIcon
                                          show={winningPositions.includes(
                                            "b38"
                                          )}
                                        />
                                        <span
                                          className={`dream_chips_cls ${
                                            b38Amt ? "" : "hides"
                                          }`}
                                        >
                                          {b38Amt}
                                        </span>
                                        {/**/}{" "}
                                        <span className="zero-value">
                                          Black
                                          {/**/}
                                        </span>
                                      </div>
                                    </div>{" "}
                                    <div
                                      className={`odd ${
                                        winningPositions.includes("b40")
                                          ? "rou-winner-cell"
                                          : ""
                                      }`}
                                      onClick={() => addToBet("b40")}
                                      onMouseEnter={() =>
                                        highlightCells(GROUP_NUMS.odd)
                                      }
                                      onMouseLeave={clearHighlight}
                                      style={{ position: "relative" }}
                                    >
                                      <TrophyIcon
                                        show={winningPositions.includes("b40")}
                                      />
                                      {/**/}{" "}
                                      <span
                                        className={`dream_chips_cls ${
                                          b40Amt ? "" : "hides"
                                        }`}
                                      >
                                        {b40Amt}
                                      </span>
                                      <span className="zero-value">
                                        Odd
                                        {/**/}
                                      </span>
                                    </div>{" "}
                                    <div
                                      className={`high ${
                                        winningPositions.includes("b42")
                                          ? "rou-winner-cell"
                                          : ""
                                      }`}
                                      onClick={() => addToBet("b42")}
                                      onMouseEnter={() =>
                                        highlightCells(GROUP_NUMS.high)
                                      }
                                      onMouseLeave={clearHighlight}
                                      style={{ position: "relative" }}
                                    >
                                      <TrophyIcon
                                        show={winningPositions.includes("b42")}
                                      />
                                      {/**/}{" "}
                                      <span
                                        className={`dream_chips_cls ${
                                          b42Amt ? "" : "hides"
                                        }`}
                                      >
                                        {b42Amt}
                                      </span>
                                      <span className="zero-value">
                                        19 - 36
                                        {/**/}
                                      </span>
                                    </div>
                                    <div
                                      style={{
                                        display:
                                          activeTabIndex == 2 ? "" : "none",
                                        color: "white"
                                      }}
                                    >
                                      <RouletteResult resultData={resultData} />
                                    </div>
                                  </div>
                                </div>{" "}
                                {/**/}
                              </div>
                            </div>
                          </>
                        )}
                        {activeTabIndex == 2 && (
                          <RouletteResult resultData={resultData} />
                        )}
                      </div>

                      {/* END LEFT COLUMN */}

                      {/* RIGHT COLUMN */}
                      <div className="col-lg-4">
                        <div className="right-content">
                          <div>
                            <div className="table-box-body">
                              {/* Mobile Modal (kept intact) */}
                              <div
                                id="betSlipMobile"
                                tabIndex="-1"
                                role="dialog"
                                aria-labelledby="fancyPosition"
                                aria-hidden="true"
                                className="modal fade"
                              >
                                <div
                                  role="document"
                                  className="modal-dialog modal-md"
                                >
                                  <div className="modal-content back">
                                    <div className="modal-body">
                                      <table>
                                        <tbody>
                                          <tr>
                                            <td>{"\u00A0"}</td>
                                            <th colSpan="3"></th>
                                          </tr>
                                          <tr>
                                            <td>ODDS</td>
                                            <td colSpan="2">
                                              <div className="input-group mt-2">
                                                <div className="input-group-prepend">
                                                  <button
                                                    type="button"
                                                    className="btn btn-outline-secondary"
                                                  >
                                                    <strong>-</strong>
                                                  </button>
                                                </div>
                                                <input
                                                  type="number"
                                                  id="bet-price"
                                                  step="0.01"
                                                  min="1.01"
                                                  max="1000"
                                                  className="form-control"
                                                />
                                                <div className="input-group-append">
                                                  <button
                                                    type="button"
                                                    className="btn btn-outline-secondary"
                                                  >
                                                    <strong>+</strong>
                                                  </button>
                                                </div>
                                              </div>
                                            </td>
                                          </tr>
                                          <tr>
                                            <td>Amount</td>
                                            <td colSpan="2">
                                              <input
                                                type="number"
                                                id="bet-size-m"
                                                className="form-control mt-2"
                                              />
                                            </td>
                                          </tr>
                                          <tr>
                                            <td style={{ width: "25%" }}>
                                              <button
                                                type="button"
                                                data-amount="10000"
                                                className="btn btn-secondary btn-block mt-2"
                                                style={{
                                                  touchAction: "manipulation"
                                                }}
                                              >
                                                10,000
                                              </button>
                                            </td>
                                            <td style={{ width: "25%" }}>
                                              <button
                                                type="button"
                                                className="btn btn-secondary btn-block mt-2"
                                                style={{
                                                  touchAction: "manipulation"
                                                }}
                                              >
                                                250,000
                                              </button>
                                            </td>
                                            <td style={{ width: "25%" }}>
                                              <button
                                                type="button"
                                                className="btn btn-secondary btn-block mt-2"
                                                style={{
                                                  touchAction: "manipulation"
                                                }}
                                              >
                                                50,000
                                              </button>
                                            </td>
                                            <td style={{ width: "25%" }}>
                                              <button
                                                type="button"
                                                className="btn btn-secondary btn-block mt-2"
                                                style={{
                                                  touchAction: "manipulation"
                                                }}
                                              >
                                                100,000
                                              </button>
                                            </td>
                                          </tr>
                                          <tr>
                                            <td>
                                              <button
                                                type="button"
                                                className="btn btn-secondary btn-block mt-2"
                                                style={{
                                                  touchAction: "manipulation"
                                                }}
                                              >
                                                +100,000
                                              </button>
                                            </td>
                                            <td>
                                              <button
                                                type="button"
                                                className="btn btn-secondary btn-block mt-2"
                                                style={{
                                                  touchAction: "manipulation"
                                                }}
                                              >
                                                +250,000
                                              </button>
                                            </td>
                                            <td>
                                              <button
                                                type="button"
                                                className="btn btn-secondary btn-block mt-2"
                                                style={{
                                                  touchAction: "manipulation"
                                                }}
                                              >
                                                +500,000
                                              </button>
                                            </td>
                                            <td>
                                              <button
                                                type="button"
                                                className="btn btn-secondary btn-block mt-2"
                                                style={{
                                                  touchAction: "manipulation"
                                                }}
                                              >
                                                +1,000,000
                                              </button>
                                            </td>
                                          </tr>
                                          <tr>
                                            <td>
                                              <button
                                                type="button"
                                                data-dismiss="modal"
                                                className="btn btn-danger mt-2"
                                                style={{
                                                  touchAction: "manipulation"
                                                }}
                                              >
                                                Close
                                              </button>
                                            </td>
                                            <td colSpan="2">
                                              <div
                                                className="btn btn-primary ld-over mt-2"
                                                style={{ cursor: "pointer" }}
                                              >
                                                <b>Submit</b>
                                                <div className="ld ld-ball ld-flip"></div>
                                              </div>{" "}
                                              <span> / -</span>
                                            </td>
                                          </tr>
                                          <tr style={{ display: "none" }}>
                                            <td
                                              colSpan="4"
                                              className="alert-danger pr-5"
                                            ></td>
                                          </tr>
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              {/* /Modal */}

                              {/* Matched Bets */}
                              <div>
                                <div className="bets">
                                  <strong>
                                    Matched Bets (0)
                                    <img
                                      src={reconnecting}
                                      alt="dc"
                                      className="rounded disconnected"
                                      style={{ display: "none" }}
                                    />{" "}
                                  </strong>
                                  <div className="betting-table">
                                    <table className="table">
                                      <thead>
                                        <tr>
                                          <th>Runner</th>
                                          <th>Price</th>
                                          <th>Size</th>
                                          <th></th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {placedBets.map((bet, index) => {
                                          console.log("MATCHED BET:", bet);

                                          return (
                                            <tr
                                              key={index}
                                              style={{
                                                background:
                                                  bet.type == "b"
                                                    ? "#e9f6fc"
                                                    : "#fce3e4"
                                              }}
                                            >
                                              <td>
                                                {displayRunnerName(bet)} - (
                                                {bet.cat_mid})
                                              </td>

                                              <td>{bet.amnt}</td>
                                              <td>{bet.rate}</td>
                                              <td></td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              </div>
                              {/* /Matched Bets */}
                            </div>
                          </div>
                        </div>
                      </div>
                      {/* END RIGHT COLUMN */}
                    </div>
                    {/* /row w-100 */}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <style>
          {`
            /* Row order: 1,4,7 on top; 2,5,8 middle; 3,6,9 bottom */
            .roulette-scope .match-odds-ctn .first-column:nth-child(1) { order: 1; z-index: 3; position: relative; }
            .roulette-scope .match-odds-ctn .first-column:nth-child(2) { order: 2; z-index: 2; position: relative; }
            .roulette-scope .match-odds-ctn .first-column:nth-child(3) { order: 3; z-index: 1; position: relative; }

            .roulette-scope .match-odds-ctn .col,
            .roulette-scope .zero-ctn {
              position: relative;
            }
            .roulette-scope .verticalOverlay,
            .roulette-scope .horizontaloverlay,
            .roulette-scope .centerboxoverlay {
              pointer-events: auto;
              cursor: pointer;
              z-index: 3;
            }
            .roulette-scope .combo-highlight {
              background-color: rgba(255, 255, 255, 0.55) !important;
              transition: background-color 0.3s ease-in;
            }
            .roulette-scope .verticalOverlay.RouDesleft {
              left: -12px !important;
              width: 24px !important;
              height: 100% !important;
              top: 0 !important;
              transform: none !important;
              z-index: 3;
            }
            .roulette-scope .verticalOverlay.RouDesRight {
              left: auto !important;
              right: -12px !important;
              width: 24px !important;
              height: 100% !important;
              top: 0 !important;
              transform: none !important;
              z-index: 3;
            }
            .roulette-scope .horizontaloverlay.RouDesCB {
              top: auto !important;
              bottom: -10px !important;
              left: 12px !important;
              width: calc(100% - 24px) !important;
              height: 20px !important;
              transform: none !important;
              z-index: 3;
            }
            .roulette-scope .centerboxoverlay.RouDesRB {
              top: auto !important;
              bottom: -10px !important;
              left: auto !important;
              right: -12px !important;
              width: 24px !important;
              height: 20px !important;
              transform: none !important;
              z-index: 4;
            }
            .roulette-scope .centerboxoverlay.RouDesLB {
              top: auto !important;
              bottom: -10px !important;
              left: -12px !important;
              width: 24px !important;
              height: 20px !important;
              transform: none !important;
              z-index: 4;
            }
            .roulette-scope .match-odds-ctn .col > .dream_chips_cls {
              z-index: 5;
              pointer-events: none;
            }
            .roulette-scope .match-odds-ctn .col > .zero-value {
              z-index: 0;
              pointer-events: none;
            }
            .roulette-scope .overlay-chip {
              height: 24px;
              width: 24px;
              min-height: 24px;
              min-width: 24px;
              font-size: 10px;
              border-width: 2px;
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%);
            }
            .roulette-scope .chipDeck .chip-ctn .chips-layer {
              justify-content: center;
              gap: 6px;
            }
            .roulette-scope .chipDeck .chip-ctn .cdChip {
              margin: 0;
              width: auto;
            }
            .roulette-scope .dream_chips_cls {
              height: 35px;
              width: 35px;
              min-height: 35px;
              min-width: 35px;
              font-size: 12px;
              border-width: 2.5px;
              border-radius: 50%;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%);
              z-index: 2;
              padding-top: 2px;
              box-sizing: border-box;
            }
            .roulette-scope .match-odds-ctn .col {
              overflow: visible;
            }
            .roulette-scope .match-odds-ctn .first-column {
              overflow: visible;
            }
            .roulette-scope .match-odds-ctn {
              overflow: visible;
            }
            .roulette-scope .dozens,
            .roulette-scope .even,
            .roulette-scope .odd,
            .roulette-scope .low,
            .roulette-scope .high,
            .roulette-scope .color-ctn {
              position: relative;
            }

            /* Desktop: square corners, solid white borders, no columns */
            .roulette-scope .roul-box-desktop .roulette-ctn {
              border-radius: 0 !important;
              overflow: visible;
              border: none;
            }
            .roulette-scope .roul-box-desktop .zero-ctn {
              border-radius: 0 !important;
              border-top-left-radius: 0 !important;
              border-bottom-left-radius: 0 !important;
            }
            .roulette-scope .zero-ctn .zero-value {
              font-size: 54px !important;
              font-weight: 700;
            }
            .roulette-scope .roul-box-desktop .columns-ctn {
              display: flex;
              flex-direction: column;
              width: 48px;
              min-width: 48px;
            }
            .roulette-scope .roul-box-desktop .columns-ctn .columns-layer {
              flex: 1;
              display: flex;
              align-items: center;
              justify-content: center;
              border-bottom: 1px solid #fff;
              border-right: 1px solid #fff;
              background: #333;
              cursor: pointer;
              position: relative;
              font-size: 13px;
              font-weight: 700;
              color: #fff;
            }
            .roulette-scope .roul-box-desktop .columns-ctn .columns-layer:last-child {
              border-bottom: none;
            }
            .roulette-scope .roul-box-desktop .match-odds-ctn {
              width: 0;
              flex: 1;
            }
            .roulette-scope .roul-box-desktop .dozens-layer .dozens-ctn,
            .roulette-scope .roul-box-desktop .last-row {
              width: 92%;
              margin-left: 8%;
            }
            .roulette-scope .roul-box-desktop .roulette-ctn {
              border: 1px solid #fff;
              border-bottom: none;
            }
            .roulette-scope .zero-ctn {
              border-right: 1px solid #fff !important;
              border-bottom: 1px solid #fff !important;
            }
            .roulette-scope .match-odds-ctn .col {
              padding: 10px 2px;
              border: none !important;
              border-image: none !important;
              border-right: 1px solid #fff !important;
              border-bottom: 1px solid #fff !important;
            }
            .roulette-scope .roul-box-desktop .dozens-layer .dozens-ctn,
            .roulette-scope .roul-box-desktop .last-row-ctn .last-row {
              border-left: 1px solid #fff;
              border-right: 1px solid #fff;
              border-bottom: 1px solid #fff;
            }
            .roulette-scope .dozens-layer .dozens-ctn .dozens,
            .roulette-scope .last-row .even,
            .roulette-scope .last-row .odd,
            .roulette-scope .last-row .low,
            .roulette-scope .last-row .high,
            .roulette-scope .last-row .color-ctn {
              border: none !important;
              border-image: none !important;
              border-right: 1px solid #fff !important;
              border-bottom: 1px solid #fff !important;
            }
            .roulette-scope .dozens-layer .dozens-ctn .dozens:last-child,
            .roulette-scope .last-row .high {
              border-right: none !important;
            }
            .roulette-scope .match-odds-ctn .first-column .col:last-child {
              border-right: none !important;
            }
            @media (max-width: 576px) {
              .roulette-scope .col-md-8 {
                flex: 0 0 100% !important;
                max-width: 100% !important;
              }
              .roulette-scope .roul-box-desktop .zero-ctn {
                font-size: 13px;
                min-width: auto;
              }
              .roulette-scope .zero-ctn .zero-value {
                font-size: 16px !important;
              }
              .roulette-scope .roul-box-desktop .dozens-layer .dozens-ctn,
              .roulette-scope .roul-box-desktop .last-row {
                width: 92% !important;
                margin-left: 8% !important;
              }
              .roulette-scope .match-odds-ctn .col {
                padding: 5px 1px;
                font-size: 12px;
              }
              .roulette-scope .dozens-layer .dozens-ctn .dozens,
              .roulette-scope .last-row .even,
              .roulette-scope .last-row .odd,
              .roulette-scope .last-row .low,
              .roulette-scope .last-row .high,
              .roulette-scope .last-row .color-ctn {
                padding: 5px 2px;
                font-size: 11px;
              }
              .roulette-scope .dream_chips_cls {
                height: 28px;
                width: 28px;
                font-size: 11px;
                border-width: 2px;
              }
              .roulette-scope .chip-odds-view-ctn {
                padding: 0 !important;
              }
              .roulette-scope .chip-odds-view-layer {
                flex-wrap: wrap;
                gap: 6px;
                margin-bottom: 6px !important;
              }
              .roulette-scope .chipDeck {
                flex: 0 0 100% !important;
                max-width: 100% !important;
                order: -1;
              }
              .roulette-scope .chipDeck .chip-ctn {
                background-image: linear-gradient(180deg, #1a1a1a, #0d0d0d), linear-gradient(270deg, #555, transparent, #555) !important;
                border-radius: 50px;
                padding: 6px 0;
                margin-bottom: 4px;
              }
              .roulette-scope .chipDeck .chip-ctn .cdChip {
                width: auto;
                margin: 0 0.5%;
              }
              .roulette-scope .chipDeck .chipimg {
                width: 38px;
                max-width: 38px;
              }
              .roulette-scope .cdChipSpan {
                font-size: 12px;
                font-weight: 700;
              }
              .roulette-scope .chip-odds-view-layer .t-l-ctn {
                flex: 1 1 0 !important;
                max-width: none !important;
                width: auto !important;
                margin-top: 4px;
              }
              .roulette-scope .chip-odds-view-layer .ure-btn {
                flex: 1 1 0 !important;
                max-width: none !important;
                width: auto !important;
                margin-top: 4px;
              }
            }

            .player-scope .table-player .player {
              width: 210px !important;
            }
            .player-scope .table-player {
              gap: 9px !important;
              align-items: center;
              display: flex;
              flex-direction: column;
              justify-content: flex-start;
              height: auto !important;
              min-height: auto !important;
            }
            .player-scope .table-player .player-dt {
              display: flex !important;
              flex-direction: column;
              align-items: center !important;
              max-width: none;
              width: auto;
              color: #d7bc7b !important;
              font-size: 12px;
              font-weight: 700;
            }
            .roulette-scope center {
              display: block;
              width: 100%;
            }
            .roulette-scope center > .footer-divbpg {
              position: relative;
              z-index: 3;
              margin-top: 10px;
              margin-bottom: 6px;
              background: linear-gradient(180deg, #464646, #0f0f0f);
              display: flex;
              flex-direction: row;
              align-items: center;
              justify-content: space-between;
              width: 100%;
              min-height: 45px;
              border: 2px solid;
              border-image-slice: 1 !important;
              border-image-source: linear-gradient(
                180deg,
                rgba(207, 141, 46, 0.47),
                #cf8d2e 50%,
                rgba(207, 141, 46, 0.47)
              );
              box-sizing: border-box;
            }
            .roulette-scope center > .footer-divbpg .buttons-div {
              align-items: center;
              display: flex;
              flex-direction: row;
              flex: 1;
              height: 100%;
              overflow-x: auto;
            }
            .roulette-scope center > .footer-divbpg .video-button1 {
              padding: 0;
              
              color: #fff !important;
              font-size: 12px !important;
              font-family: 'Roboto Condensed';
              font-weight: 500;
              height: 35px !important;
              min-width: 45px;
              width: auto !important;
              margin-right: 1px;
              text-align: center; 
              border: 3px solid #a8a8a8 !important; 
              border-radius: 0 !important;
              flex-shrink: 0;
              pointer-events: none;
            }
            .roulette-scope center > .footer-divbpg .round-div {
              margin-right: 4px;
              color: #fff;
              display: flex;
              flex-direction: column;
              font-size: 11px;
              justify-content: center;
              white-space: nowrap;
              flex-shrink: 0;
            }
            .roulette-scope center > .footer-divbpg .round-div .label {
              color: rgba(255, 255, 255, 0.85);
            }
            .roulette-scope .font-familyAVE.roulette-bgNew {
              position: relative;
              background-color: #000000;
              background-image:
                radial-gradient(
                  ellipse 100% 44% at 50% 50%,
                  rgba(62, 193, 65, 0.29) 0%,
                  rgba(62, 193, 65, 0.24) 15%,
                  rgba(30, 80, 35, 0.15) 40%,
                  rgba(30, 30, 30, 0.41) 55%,
                  rgba(30, 30, 30, 0.53) 100%
                ),
                url("${rouletBg}");
              background-repeat: no-repeat, round;
              background-size: cover, auto;
              background-position: center, center;
            }
            .roulette-scope .font-familyAVE.roulette-bgNew > * {
              position: relative;
              z-index: 1;
            }
            .roulette-scope .roulli-wheel {
              padding-top: 15px;
              padding-bottom: 15px;
            }
            .roulette-scope .roulette-pinR {
              top: calc(-10% + 10px);
            }
            .roulette-scope .row .col-2.col-md-1 {
              position: relative;
            }
            .roulette-scope .Connectedclients {
              position: absolute;
              top: 6px;
              left: 28px;
            }
            .roulette-scope .cards-wrapper {
              margin-bottom: 10px;
            }
            .roulette-scope .volume-wrapper {
              position: relative;
              z-index: 4;
              height: 0;
              margin-top: -40px;
              margin-bottom: 0;
            }
            .roulette-scope .volume-wrapper .sound-icon {
              position: relative;
              top: -30px;
              left: 5px;
            }
            @media (min-width:992px){
              .roulette-scope .volume-wrapper {
                margin-top: -40px;
              }
            }
            /* mobile */
            @media (max-width:576px){
              .roulette-scope .col-md-8{
                padding-right: 5px;
                padding-left: 5px;
              }
              .roulette-scope .roulette-pinR {
                top: calc(-10% + 10px) !important;
              }
              .roulette-scope .roulette-wheelR {
                top: -10px;
              }
              .roulette-scope .volume-wrapper{
                margin-top: -20px;
              }
              .roulette-scope .col-lg-4{
                padding-right: 0px;
                padding-left: 0px;
                margin-top:10px;
              }
            }
              
            .col-Red-button {
              background-color: #e42d40 ;
              padding: 0;
    color: #fff !important;
    font-size: 12px !important;
    font-family: 'Roboto Condensed';
    font-weight: 500;
    height: 35px !important;
    min-width: 45px;
    width: auto !important;
    margin-right: 1px;
    text-align: center;
    border: 3px solid #a8a8a8 !important;
    border-radius: 0 !important;
    flex-shrink: 0;
    pointer-events: none;
          }
          .col-Black-button {
            background-color: black ;
            padding: 0;
    color: #fff !important;
    font-size: 12px !important;
    font-family: 'Roboto Condensed';
    font-weight: 500;
    height: 35px !important;
    min-width: 45px;
    width: auto !important;
    margin-right: 1px;
    text-align: center;
    border: 3px solid #a8a8a8 !important;
    border-radius: 0 !important;
    flex-shrink: 0;
    pointer-events: none;
        }
          `}
        </style>
        <style>
          {Object.entries(chipColorMap)
            .map(([key, color]) => {
              if (key.startsWith("c_")) return "";
              const num = key.replace("b", "");
              const n = parseInt(num);
              if (n <= 36)
                return `#value-${num} > .dream_chips_cls { background: ${color} !important; }`;
              const sel = {
                37: ".col-Red.color-ctn",
                38: ".col-Black.color-ctn",
                39: ".even",
                40: ".odd",
                41: ".low",
                42: ".high",
                43: ".dozens-ctn .dozens:nth-child(1)",
                44: ".dozens-ctn .dozens:nth-child(2)",
                45: ".dozens-ctn .dozens:nth-child(3)",
                46: ".columns-ctn .columns-layer:nth-child(1)",
                47: ".columns-ctn .columns-layer:nth-child(2)",
                48: ".columns-ctn .columns-layer:nth-child(3)"
              };
              return sel[num]
                ? `.roulette-scope ${sel[num]} > .dream_chips_cls { background: ${color} !important; }`
                : "";
            })
            .join("\n")}
        </style>
      </ClientLayoutWithOutHeader>
    </div>
  );
}
